import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import StopIcon from "@mui/icons-material/Stop";
import "./PrinterAgent.css";
import Navbar from "../../../../posnavbar/Navbar";
import {
  connectPrinterAgent,
  getAllPrinterConfigs,
  stopPrinterAgent,
  stopForcePrinterAgent,
  testPrintAgent,
  refreshPrinterConfig,
} from "../../../../../../config/Services";
import { Printer, Text, Line, Cut, render } from "react-thermal-printer";
import { usePrinterAgentStatus } from "../../../../../../context/PrinterAgentContext";
import { useSnackbar } from "../../../hooks/SnackbarProvider";

const PrinterAgent = () => {
  const {
    isAgentAvailable,
    isAgentRunning,
    isChecking,
    hasInitialCheckDone,
    lastChecked,
    error,
    isPolling,
    showStartButton,
    showEnableButton,
    initializePrinterAgent,
    checkPrinterAgentStatus,
    retryConnection,
    setConnected,
    setDisconnected,
    startPolling,
    startAgent,
  } = usePrinterAgentStatus();

  const showSnackbar = useSnackbar();

  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [starting, setStarting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingPrinter, setTestingPrinter] = useState(false);
  const [testingPrinterId, setTestingPrinterId] = useState(null);
  const [forceStopping, setForceStopping] = useState(false);
  const [refreshingConfig, setRefreshingConfig] = useState(false);
  const [locationMismatch, setLocationMismatch] = useState(false);

  const PRINTER_AGENT_DOWNLOAD_URL =
    process.env.MY_ENV === "production"
      ? "https://storage.googleapis.com/twinleaves_bucket/printer-agent/printer-agent.zip"
      : "https://storage.googleapis.com/twinleaves_stage_public/printer-agents/printer-agent.zip";

  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));
  const access_token = localStorage.getItem("access_token");

  const getConnectionPayload = () => ({
    locationId: locId,
    orgId: orgId,
    at: access_token,
    createdBy: retailUserLocalStorageData?.uidx,
    createdByName: retailUserLocalStorageData?.firstName,
  });

  useEffect(() => {
    const initPage = async () => {
      loadPrinterConfigs();
      // Always check status when entering the printer agent page
      const result = await checkPrinterAgentStatus();
      // Start polling if no HTTPS error
      if (result.success) {
        startPolling();
      } else if (result.isHttpsError) {
        // Show snackbar only once when HTTPS error occurs
        showSnackbar(
          "Printer agent is not running. Please run the agent in your system first.",
          "error"
        );
      }
    };
    initPage();
  }, []);

  const mapPrinterData = (printerConfig) => {
    return {
      id: printerConfig.printerConfigId,
      name: printerConfig.printerName,
      type: printerConfig.printerRouting || printerConfig.printerUsageType || "General",
      ipAddress: printerConfig.ipAddress,
      lastPrintJob: printerConfig.updated || new Date().toISOString(),
      model: printerConfig.printerModel,
      brand: printerConfig.printerBrandName,
      prepStation: printerConfig.prepStation,
      prepStationName: printerConfig.prepStationName || "-",
      portNumber: printerConfig.portNumber,
      isDefault: printerConfig.isDefaultPrinter || false,
    };
  };

  const loadPrinterConfigs = async () => {
    try {
      setLoading(true);
      setLocationMismatch(false);
      const printersResponse = await getAllPrinterConfigs();

      if (
        printersResponse &&
        (printersResponse?.data?.statusCode === 200 || printersResponse?.data?.es === 0)
      ) {
        const responseData = printersResponse?.data?.data;
        const responseOrgId = responseData?.orgId;
        const responseLocationId = responseData?.locationId;

        // Check if orgId and locationId match with localStorage
        if (responseOrgId && responseLocationId) {
          if (responseOrgId !== orgId || responseLocationId !== locId) {
            setLocationMismatch(true);
            setDisconnected();
            setAgentStatus({
              version: "1.2.3",
              lastHeartbeat: new Date().toISOString(),
              printers: [],
            });
            setLoading(false);
            return;
          }
        }

        const printers = responseData?.printers || [];
        const mappedPrinters = printers?.map(mapPrinterData);

        setAgentStatus({
          version: "1.2.3",
          lastHeartbeat: new Date().toISOString(),
          printers: mappedPrinters,
        });
      } else {
        setAgentStatus({
          version: "1.2.3",
          lastHeartbeat: new Date().toISOString(),
          printers: [],
        });
      }
      setLoading(false);
    } catch (err) {
      setAgentStatus({
        version: "1.2.3",
        lastHeartbeat: new Date().toISOString(),
        printers: [],
      });
      setLoading(false);
    }
  };

  // Handle Start Agent button click - called when agent is not available
  const handleStartAgent = async () => {
    try {
      setStarting(true);
      setConnectionStatus(null);

      const payload = getConnectionPayload();
      const connectResponse = await connectPrinterAgent(payload);

      if (
        connectResponse &&
        (connectResponse?.data?.statusCode === 200 || connectResponse?.data?.es === 0)
      ) {
        await loadPrinterConfigs();
        await checkPrinterAgentStatus();

        setConnectionStatus({
          type: "success",
          message:
            "Printer agent started successfully! Kitchen orders will now be sent to printer.",
        });
      } else {
        throw new Error(connectResponse?.message || "Failed to connect to printer agent");
      }
    } catch (err) {
      setConnectionStatus({
        type: "error",
        message: `Failed to start printer agent: ${err.message}. Make sure the agent application is running on your computer.`,
      });
    } finally {
      setStarting(false);
    }
  };

  // Handle Enable Agent button click - called when agent is disabled
  const handleEnableAgent = async () => {
    try {
      setToggling(true);
      setConnectionStatus(null);

      const response = await stopPrinterAgent(true); // true = enable

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        // Set localStorage as connected
        setConnected();
        await checkPrinterAgentStatus();

        setConnectionStatus({
          type: "success",
          message: "Printer agent enabled! Kitchen orders will now be sent to printer.",
        });
      } else {
        throw new Error(response?.message || "Failed to enable printer agent");
      }
    } catch (err) {
      setConnectionStatus({
        type: "error",
        message: `Failed to enable printer agent: ${err.message}`,
      });
    } finally {
      setToggling(false);
    }
  };

  // Handle Disable Agent - called when agent is running
  const handleDisableAgent = async () => {
    try {
      setToggling(true);
      setConnectionStatus(null);

      const response = await stopPrinterAgent(false); // false = disable

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        // Set localStorage as disconnected
        setDisconnected();
        await checkPrinterAgentStatus();

        setConnectionStatus({
          type: "info",
          message: "Printer agent disabled. Kitchen orders will not be printed.",
        });
      } else {
        throw new Error(response?.message || "Failed to disable printer agent");
      }
    } catch (err) {
      setConnectionStatus({
        type: "error",
        message: `Failed to disable printer agent: ${err.message}`,
      });
    } finally {
      setToggling(false);
    }
  };

  // Handle Force Stop Agent - completely stops the printer agent
  const handleForceStop = async () => {
    try {
      setForceStopping(true);
      setConnectionStatus(null);

      const response = await stopForcePrinterAgent();

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        // Set localStorage as disconnected
        setDisconnected();

        setConnectionStatus({
          type: "info",
          message: "Printer agent has been force stopped completely.",
        });
      } else {
        throw new Error(response?.message || "Failed to force stop printer agent");
      }
    } catch (err) {
      setConnectionStatus({
        type: "error",
        message: `Failed to force stop printer agent: ${err.message}`,
      });
    } finally {
      setForceStopping(false);
      // Always check status after force stop to update UI
      await checkPrinterAgentStatus();
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = PRINTER_AGENT_DOWNLOAD_URL;
    link.download = "printer-agent-installer.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Refresh Printer Config
  const handleRefreshPrinterConfig = async () => {
    try {
      setRefreshingConfig(true);
      setConnectionStatus(null);

      const response = await refreshPrinterConfig();

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        await loadPrinterConfigs();
        setConnectionStatus({
          type: "success",
          message: "Printer configuration refreshed successfully.",
        });
      } else {
        throw new Error(response?.data?.message || "Failed to refresh printer configuration");
      }
    } catch (err) {
      setConnectionStatus({
        type: "error",
        message: `Failed to refresh printer configuration: ${err.message}`,
      });
    } finally {
      setRefreshingConfig(false);
    }
  };

  // Handle Refresh Status - calls both checkPrinterAgentStatus and refreshPrinterConfig
  const handleRefreshStatus = async () => {
    try {
      // Call both APIs in parallel
      const [statusResult] = await Promise.all([
        checkPrinterAgentStatus(),
        // refreshPrinterConfig(),
      ]);

      // Reload printer configs after refresh
      await loadPrinterConfigs();

      return statusResult;
    } catch (err) {}
  };

  // Handle Test Printer for a specific printer
  const handleTestPrinterById = async (printer) => {
    try {
      setTestingPrinterId(printer.id);
      setConnectionStatus(null);

      const ipAddress = printer.ipAddress;
      const portNumber = printer.portNumber || 9100;

      const testOrderItems = [
        { productName: "Test Item 1", variantName: "Regular", quantityBySpecs: 2 },
        { productName: "Test Item 2", variantName: "Large", quantityBySpecs: 1 },
      ];

      const printerWidth = 42;
      const testContent = await render(
        <Printer type="epson" width={printerWidth}>
          <Text align="center" bold size={{ width: 2, height: 2 }}>
            TEST PRINT
          </Text>
          <Line />
          <Text align="center" bold size={{ width: 2, height: 2 }}>
            KOT: 999 - T1
          </Text>
          <Line />
          <Text bold>Customer: Test Customer</Text>
          <Text>Order Type: DINE_IN</Text>
          <Line />
          <Text bold>Items:</Text>
          {testOrderItems.map((item, index) => (
            <Text key={index}>
              {`${item.quantityBySpecs}x ${item.productName} (${item.variantName})`}
            </Text>
          ))}
          <Line />
          <Text align="center">{new Date().toLocaleString()}</Text>
          <Text align="center">Printer Agent Test</Text>
          <Line />
          <Cut />
        </Printer>
      );

      const base64Content = btoa(String.fromCharCode(...new Uint8Array(testContent)));

      const payload = {
        ip: ipAddress,
        port: portNumber,
        content: base64Content,
      };

      const response = await testPrintAgent(payload);

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        setConnectionStatus({
          type: "success",
          message: `Test print sent successfully to ${printer.name} (${ipAddress}:${portNumber})`,
        });
      } else {
        throw new Error(response?.data?.message || "Failed to send test print");
      }
    } catch (err) {
      setConnectionStatus({
        type: "error",
        message: `Test print failed: ${err.message}. Make sure the printer is connected.`,
      });
    } finally {
      setTestingPrinterId(null);
    }
  };

  // Handle Test Printer (uses first printer - legacy function)
  const handleTestPrinter = async () => {
    if (!agentStatus?.printers || agentStatus.printers.length === 0) {
      setConnectionStatus({
        type: "error",
        message: "No printers configured. Please configure a printer first.",
      });
      return;
    }
    setTestingPrinter(true);
    await handleTestPrinterById(agentStatus.printers[0]);
    setTestingPrinter(false);
  };

  const getStatusDisplay = () => {
    if (isChecking && !hasInitialCheckDone) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">Checking status...</Typography>
        </Box>
      );
    }

    if (!isAgentAvailable) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorIcon sx={{ color: "#f44336" }} />
          <Typography variant="body2" sx={{ color: "#f44336" }}>
            Agent Not Running
          </Typography>
        </Box>
      );
    }

    if (locationMismatch) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorIcon sx={{ color: "#ff9800" }} />
          <Typography variant="body2" sx={{ color: "#ff9800" }}>
            Not Running for this Location
          </Typography>
        </Box>
      );
    }

    if (!isAgentRunning) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorIcon sx={{ color: "#ff9800" }} />
          <Typography variant="body2" sx={{ color: "#ff9800" }}>
            Agent Disabled
          </Typography>
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CheckCircleIcon sx={{ color: "#4caf50" }} />
        <Typography variant="body2" sx={{ color: "#4caf50" }}>
          Agent Running
        </Typography>
      </Box>
    );
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const hasPrinters = agentStatus?.printers && agentStatus.printers.length > 0;

  return (
    <Navbar>
    <Box className="printer-agent-container">
      <Typography variant="h5" className="printer-agent-title" gutterBottom>
        Printer Agent
      </Typography>

      <Typography variant="body2" color="textSecondary" paragraph>
        Download and install the Printer Agent to enable seamless printing from your POS system.
      </Typography>

      {connectionStatus && (
        <Alert
          severity={connectionStatus.type}
          onClose={() => setConnectionStatus(null)}
          sx={{ mb: 2 }}
        >
          {connectionStatus.message}
        </Alert>
      )}

      {locationMismatch && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The agent is not running for this location. Please start the agent for this location.
        </Alert>
      )}

      <Paper elevation={2} className="status-section">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Agent Status
          </Typography>
          {getStatusDisplay()}
        </Box>

        <Box display="flex" flexDirection="column" gap={1} mb={2}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="textSecondary">
              Status:
            </Typography>
            <Chip
              label={
                !isAgentAvailable
                  ? "Not Running"
                  : locationMismatch
                  ? "Wrong Location"
                  : !isAgentRunning
                  ? "Disabled"
                  : "Online"
              }
              size="small"
              color={
                !isAgentAvailable
                  ? "error"
                  : locationMismatch
                  ? "warning"
                  : !isAgentRunning
                  ? "warning"
                  : "success"
              }
            />
          </Box>
          {agentStatus && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Connected Printers:
              </Typography>
              <Chip label={agentStatus.printers?.length || 0} size="small" color="primary" />
            </Box>
          )}
          {lastChecked && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Last Checked:
              </Typography>
              <Typography variant="body2">{new Date(lastChecked).toLocaleTimeString()}</Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="textSecondary">
              Auto Refresh:
            </Typography>
            <Chip
              label={isPolling ? "Active (30s)" : "Inactive"}
              size="small"
              color={isPolling ? "success" : "default"}
              variant={isPolling ? "filled" : "outlined"}
            />
          </Box>
          {isChecking && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="textSecondary">
                Checking status...
              </Typography>
            </Box>
          )}
        </Box>

        {/* 🔴 Show error when agent is running but no printers are configured */}
        {hasInitialCheckDone &&
          isAgentAvailable &&
          isAgentRunning &&
          agentStatus &&
          (!agentStatus.printers || agentStatus.printers.length === 0) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              There are no printers configured for this agent. Please configure printers from RMS.
            </Alert>
          )}

        {/* Show error/retry when agent is not available (HTTPS error) */}
        {!isAgentAvailable && error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={retryConnection}
              >
                Retry
              </Button>
            }
          >
            Printer agent is not running in your system. Please start the agent application first.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Box display="flex" gap={2} flexWrap="wrap">
          {/* Show Start Agent button only when data has more than application-status (showStartButton is true) or location mismatch */}
          {isAgentAvailable && (showStartButton || locationMismatch) && (
            <Button
              variant="contained"
              color="success"
              startIcon={
                starting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />
              }
              onClick={handleStartAgent}
              disabled={starting}
              sx={{ flex: 1, minWidth: "140px" }}
            >
              {starting ? "Starting Agent..." : "Start Agent"}
            </Button>
          )}

          {/* Show Enable Agent button when data contains only application-status (agent is disabled) */}
          {isAgentAvailable && showEnableButton && (
            <Button
              variant="contained"
              color="success"
              startIcon={
                toggling ? <CircularProgress size={20} color="inherit" /> : <PowerSettingsNewIcon />
              }
              onClick={handleEnableAgent}
              disabled={toggling}
              sx={{ flex: 1, minWidth: "140px" }}
            >
              {toggling ? "Enabling..." : "Enable Agent"}
            </Button>
          )}

          {/* Show Disable button if agent is available and running (data is empty) and no location mismatch */}
          {isAgentAvailable && isAgentRunning && !showStartButton && !locationMismatch && (
            <Button
              variant="outlined"
              color="error"
              startIcon={
                toggling ? <CircularProgress size={20} color="inherit" /> : <PowerSettingsNewIcon />
              }
              onClick={handleDisableAgent}
              disabled={toggling}
              sx={{ flex: 1, minWidth: "140px" }}
            >
              {toggling ? "Disabling..." : "Disable Agent"}
            </Button>
          )}

          {isAgentAvailable && isAgentRunning && !showStartButton && !locationMismatch && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                starting ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
              }
              onClick={handleStartAgent}
              disabled={starting}
              sx={{ flex: 1, minWidth: "140px" }}
            >
              {starting ? "Refreshing" : "Refresh Creds"}
            </Button>
          )}

          {/* Force Stop Button - only when agent is available and no location mismatch */}
          {isAgentAvailable && !locationMismatch && (
            <Button
              variant="contained"
              color="error"
              startIcon={
                forceStopping ? <CircularProgress size={20} color="inherit" /> : <StopIcon />
              }
              onClick={handleForceStop}
              disabled={forceStopping}
              sx={{ flex: 1, minWidth: "140px" }}
            >
              {forceStopping ? "Force Stopping..." : "Force Stop"}
            </Button>
          )}

          {/* Refresh Button - visible when no location mismatch */}
          {!locationMismatch && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                isChecking ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
              }
              onClick={handleRefreshStatus}
              disabled={isChecking}
              sx={{ flex: 1, minWidth: "140px" }}
            >
              {isChecking ? "Refreshing..." : "Refresh Status"}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Printers Status Section */}
      {agentStatus && agentStatus.printers && agentStatus.printers.length > 0 && (
        <Paper elevation={2} className="printers-section" sx={{ mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Configured Printers
            </Typography>
            {isAgentAvailable && isAgentRunning && (
              <Tooltip title="Refresh printer configuration">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleRefreshPrinterConfig}
                  disabled={refreshingConfig}
                >
                  {refreshingConfig ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Printer Name</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell>Prep Station</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Port</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agentStatus.printers.map((printer) => (
                  <TableRow
                    key={printer.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PrintIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">{printer.name}</Typography>
                            {printer.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                                sx={{ height: 18, fontSize: "0.65rem" }}
                              />
                            )}
                          </Box>
                          {printer.model && (
                            <Typography variant="caption" color="textSecondary">
                              {printer.brand} {printer.model}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={printer.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{printer.prepStationName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {printer.ipAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {printer.portNumber || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={`Test print to ${printer.ipAddress}:${printer.portNumber || 9100}`}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            startIcon={
                              testingPrinterId === printer.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <PrintIcon />
                              )
                            }
                            onClick={() => handleTestPrinterById(printer)}
                            disabled={testingPrinterId !== null || !isAgentRunning}
                          >
                            {testingPrinterId === printer.id ? "Testing..." : "Test"}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Download Section */}
      <Paper elevation={2} className="download-section">
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Download Printer Agent
        </Typography>
        <Typography variant="body2" paragraph sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
          Click the button below to download the Printer Agent ZIP file.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          fullWidth
          className="download-button"
        >
          Download Printer Agent (Windows)
        </Button>
      </Paper>

      {/* Instructions Section */}
      <Paper elevation={2} className="instructions-section">
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <InfoIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">
            Installation Instructions
          </Typography>
        </Box>

        <Box className="instruction-steps">
          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              1
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Download the Agent
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Click the download button above to get the Printer Agent ZIP file.
              </Typography>
            </Box>
          </Box>

          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              2
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Extract All Files
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Locate the downloaded ZIP file and extract all files to a folder.
              </Typography>
            </Box>
          </Box>

          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              3
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Open Printer Agent Folder
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Navigate to the extracted folder and go into the "printer-agent" folder.
              </Typography>
            </Box>
          </Box>

          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              4
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Run as Administrator
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Select the "start" file, right-click and choose "Run as administrator". Click "Yes"
                when prompted.
              </Typography>
              <Box
                component="img"
                src="https://storage.googleapis.com/twinleaves_stage_public/printer-agents/run.png"
                alt="Run as Administrator"
                sx={{
                  mt: 1,
                  maxWidth: "100%",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            </Box>
          </Box>

          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              5
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Complete Setup in Terminal
              </Typography>
              <Typography variant="body2" color="textSecondary">
                A terminal window will open. When "continue" appears, press Enter to proceed.
              </Typography>
              <Box
                component="img"
                src="https://storage.googleapis.com/twinleaves_stage_public/printer-agents/active-app.png"
                alt="Terminal Continue"
                sx={{
                  mt: 1,
                  maxWidth: "100%",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            </Box>
          </Box>

          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              6
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Start Agent
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Return to this screen and click "Start Agent" button to connect your printers. If
                enable button is present, click to enable the agent.
              </Typography>
            </Box>
          </Box>

          <Box className="instruction-step">
            <Typography variant="body2" className="step-number">
              7
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight="500">
                Stop Agent
              </Typography>
              <Typography variant="body2" color="textSecondary">
                To Stop the agent click on force stop.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Troubleshooting Section */}
      <Paper elevation={2} className="troubleshooting-section">
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Troubleshooting
        </Typography>

        <Box className="troubleshooting-list">
          <Typography variant="body2" className="troubleshooting-item">
            • Make sure your firewall allows the Printer Agent (port 8442)
          </Typography>
          <Typography variant="body2" className="troubleshooting-item">
            • Ensure your printer is properly connected and powered on
          </Typography>
          <Typography variant="body2" className="troubleshooting-item">
            • Try restarting the Printer Agent application
          </Typography>
          <Typography variant="body2" className="troubleshooting-item">
            • Verify the printer IP addresses are correct
          </Typography>
        </Box>
      </Paper>
    </Box>
    </Navbar>
  );
};

export default PrinterAgent;
